import { CheckCircle2, Info, OctagonAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Toaster as Sonner, toast } from 'sonner';
import { cn } from '../lib/cn';

/** Mount once near the root. Bottom-right, non-blocking, announced to screen readers. */
export function Toaster() {
  return (
    <Sonner
      position="bottom-right"
      offset={20}
      visibleToasts={4}
      toastOptions={{ duration: 4500 }}
      style={
        {
          '--normal-bg': 'var(--surface)',
          '--normal-text': 'var(--fg)',
          '--normal-border': 'var(--border)',
          '--border-radius': '0.625rem',
        } as React.CSSProperties
      }
    />
  );
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface RichToastProps {
  tone: 'success' | 'error' | 'info';
  title: string;
  description?: ReactNode;
  /** Plain-language cause: "Backup folder is unavailable." */
  reason?: string;
  actions?: ToastAction[];
  onDismiss: () => void;
}

const toneStyle = {
  success: { Icon: CheckCircle2, cls: 'text-success-fg' },
  error: { Icon: OctagonAlert, cls: 'text-danger-fg' },
  info: { Icon: Info, cls: 'text-info-fg' },
} as const;

function RichToast({ tone, title, description, reason, actions, onDismiss }: RichToastProps) {
  const { Icon, cls } = toneStyle[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className="pointer-events-auto flex w-[22rem] max-w-[calc(100vw-2rem)] gap-3 rounded-lg border bg-surface p-3.5 shadow-lg"
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', cls)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-fg">{title}</p>
        {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
        {reason ? (
          <p className="mt-1.5 text-sm text-muted">
            <span className="font-medium text-fg">Reason: </span>
            {reason}
          </p>
        ) : null}
        {actions?.length ? (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => {
                  a.onClick();
                  onDismiss();
                }}
                className="h-8 rounded-md border border-border-strong bg-surface px-3 text-sm font-medium text-fg hover:bg-surface-2"
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <button type="button" aria-label="Dismiss notification" onClick={onDismiss} className="-mr-1 -mt-1 grid size-7 shrink-0 place-items-center rounded-md text-subtle hover:bg-sunken hover:text-fg">
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}

function show(tone: RichToastProps['tone'], title: string, opts: Omit<RichToastProps, 'tone' | 'title' | 'onDismiss'> & { duration?: number } = {}) {
  const { duration, ...rest } = opts;
  return toast.custom((id) => <RichToast tone={tone} title={title} onDismiss={() => toast.dismiss(id)} {...rest} />, {
    duration: duration ?? (tone === 'error' || rest.actions?.length ? 9000 : 4500),
  });
}

/**
 * Feedback pattern (brief section 58): short success confirmations; failures explain the reason
 * in plain language and offer next actions. Never show raw error text or stack traces.
 */
export const notify = {
  success: (title: string, description?: ReactNode) => show('success', title, { description }),
  info: (title: string, description?: ReactNode) => show('info', title, { description }),
  error: (title: string, opts: { reason?: string; description?: ReactNode; actions?: ToastAction[] } = {}) => show('error', title, opts),
  dismiss: (id?: string | number) => toast.dismiss(id),
};
